import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Formik, Field } from 'formik';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import SharedPortalGroupProps from 'propTypes/sharedPortalGroups';

import ModalComponent from 'components/ModalComponent';
import ValidationErrors from 'components/forms/ValidationErrors';

import './ManageSharedPortalGroups.scss';
import FontAwesome from '../../utils/FA';

const ManageSharedPortalGroupModal = ({
  customers,
  id,
  genderConfig,
  genderOptions,
  ethnicityOptions,
  subdomain,
  location,
  customBackgroundUrl,
  customLogoUrl,
  name,
  customerIds,
  demo,
  householdOptional,
  ethnicityOptional,
  genderOptional,
  householdHidden,
  ethnicityHidden,
  genderHidden,
  displayInExplore,
  displayInBrandedSharedPortal,
  onSubmit,
  google_analytics_4_code,
}) => {
  const initialValues = {
    name,
    subdomain,
    location,
    demo,
    custom_background_url: customBackgroundUrl,
    custom_logo_url: customLogoUrl,
    customer_ids: customerIds,
    gender_options: genderOptions,
    ethnicity_options: ethnicityOptions,
    household_optional: householdOptional,
    ethnicity_optional: ethnicityOptional,
    gender_optional: genderOptional,
    household_hidden: householdHidden,
    ethnicity_hidden: ethnicityHidden,
    gender_hidden: genderHidden,
    display_in_branded_shared_portal: displayInBrandedSharedPortal,
    display_in_explore: displayInExplore,
    google_analytics_4_code,
  };

  if (id) {
    initialValues.id = id;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        const processedValues = {
          ...values,
          ...(values.display_in_branded_shared_portal
            ? {}
            : {
              custom_background_url: null,
              custom_logo_url: null,
              subdomain: null
            })
        };

        onSubmit(processedValues, { setErrors, setSubmitting });
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({
        values,
        errors,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        getFieldProps
      }) => {
        const buttons = [
          <Button
            variant='primary'
            type='submit'
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {
              isSubmitting ? 'Submitting' : 'Submit'
            }
          </Button>
        ];

        const customBackgroundFieldValue = getFieldProps('custom_background').value;
        const customLogoFieldValue = getFieldProps('custom_logo').value;

        const toggleBoolean = (fieldName) => (() => setFieldValue(fieldName, !values[fieldName]));
        const handleUpload = (fieldName, file) => {
          setFieldValue(fieldName, file)
        };

        return (
          <ModalComponent
            title='Edit School Group Details'
            className='manage-shared-portal-group-modal'
            buttons={buttons}
          >
            <form>
              <div className='form-horizontal'>
                <ValidationErrors errors={errors} fieldNames={{}} />

                <div className={classNames('mb-3 row', errors.name ? 'has-error' : null)}>
                  <Form.Label htmlFor='name'>Name</Form.Label>
                  <div className='controls'>
                    <Form.Control
                      name='name'
                      type='text'
                      onChange={handleChange}
                      value={values.name}
                    />
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='portal_type'>
                    Portal Type
                  </Form.Label>

                  <div className='controls'>
                    <Form.Check
                      id='display_in_explore'
                      type='checkbox'
                      label='Explore'
                      name='display_in_explore'
                      defaultChecked={values.display_in_explore}
                      onChange={toggleBoolean('display_in_explore')}
                    />
                    <Form.Check
                      id='display_in_branded_shared_portal'
                      type='checkbox'
                      label='Branded Shared Portal'
                      name='display_in_branded_shared_portal'
                      defaultChecked={values.display_in_branded_shared_portal}
                      onChange={toggleBoolean('display_in_branded_shared_portal')}
                    />
                  </div>
                </div>

                {values.display_in_branded_shared_portal && (
                  <div className='branded-portal-fields mb-3'>
                    <h5 className='branded-portal-fields-title'>
                      Branded Shared Portal Settings
                    </h5>
                    <p className='text-muted'>
                      These settings are specific to the Branded Shared Portal. They will only be active if this feature
                      is enabled.
                    </p>

                    <div className={classNames('mb-3 row', errors.name ? 'has-error' : null)}>
                      <Form.Label htmlFor='subdomain'>Subdomain</Form.Label>
                      <div className='controls'>
                        <Form.Control
                          name='subdomain'
                          type='text'
                          onChange={handleChange}
                          value={values.subdomain}
                        />
                      </div>
                    </div>

                    <div className={classNames('mb-3 row', errors.custom_background ? 'has-error' : null)}>
                      <Form.Label htmlFor='custom_background'>Custom Background</Form.Label>
                      <div className='controls'>
                        {customBackgroundUrl && (customBackgroundFieldValue == null) && (
                          <img
                            src={customBackgroundUrl}
                            alt='Custom Background'
                            className='manage-shared-portal-group-modal-schools-custom-background'
                          />
                        )}
                        {customBackgroundFieldValue && (
                          <img
                            src={URL.createObjectURL(customBackgroundFieldValue)}
                            alt='Custom Background'
                            className='manage-shared-portal-group-modal-schools-custom-background'
                          />
                        )}
                        <Form.Control
                          name='custom_background'
                          type='file'
                          onChange={(e) => handleUpload('custom_background', e.target.files[0])}
                        />
                      </div>
                    </div>

                    <div className={classNames('mb-3 row', errors.custom_logo ? 'has-error' : null)}>
                      <Form.Label htmlFor='custom_logo'>Custom Logo</Form.Label>
                      <div className='controls'>
                        {customLogoUrl && (customLogoFieldValue == null) && (
                          <img
                            src={customLogoUrl}
                            alt='Custom Logo'
                            className='manage-shared-portal-group-modal-schools-custom-logo'
                          />
                        )}
                        {customLogoFieldValue && (
                          <img
                            src={URL.createObjectURL(customLogoFieldValue)}
                            alt='Custom Logo'
                            className='manage-shared-portal-group-modal-schools-custom-logo'
                          />
                        )}
                        <Form.Control
                          name='custom_logo'
                          type='file'
                          onChange={(e) => handleUpload('custom_logo', e.target.files[0])}
                        />
                      </div>
                    </div>

                    <div className={classNames('mb-3 row', errors.custom_logo ? 'has-error' : null)}>
                      <Form.Label htmlFor='custom_logo'>Google Analytics 4 (GA4) Tracking ID</Form.Label>
                      <div className='controls'>
                        <Form.Control
                          name='google_analytics_4_code'
                          type='text'
                          onChange={handleChange}
                          value={values.google_analytics_4_code}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className={classNames('mb-3 row', errors.location ? 'has-error' : null)}>
                  <Form.Label htmlFor='location'>Location</Form.Label>
                  <div className='controls'>
                    <Form.Control
                      name='location'
                      type='text'
                      onChange={handleChange}
                      value={values.location}
                    />
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='customer_ids'>Members</Form.Label>
                  <div className='controls'>
                    <Field
                      name='customer_ids'
                    >
                      {
                        () => (
                          <Select
                            clearable
                            multi
                            className='manage-shared-portal-group-modal-schools-customer-ids'
                            value={values.customer_ids}
                            options={
                              customers.ready ? customers.map((customer) => ({
                                value: customer.id,
                                label: customer.subdomain
                              })) : []
                            }
                            onChange={
                              (selectedCustomers) => setFieldValue('customer_ids',
                                selectedCustomers.map(({ value }) => (value)))
                            }
                          />
                        )
                      }
                    </Field>
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='gender_options'>Gender Options</Form.Label>
                  <div className='controls'>
                    {!values.gender_hidden && (
                      <Field
                        name='gender_options'
                      >
                        {
                          () => (
                            <Select
                              clearable
                              multi
                              className='manage-shared-portal-group-modal-schools-gender-options'
                              value={values.gender_options ? values.gender_options.split(',') : []}
                              options={
                                genderConfig ? Object.keys(genderConfig.gender_map).map((gender) => ({
                                  value: gender,
                                  label: genderConfig.gender_map[gender]
                                })) : []
                              }
                              onChange={
                                (selectedGenders) => setFieldValue('gender_options',
                                  selectedGenders.map(({ value }) => (value)).join(','))
                              }
                            />
                          )
                        }
                      </Field>
                    )}
                    {!values.gender_hidden && (
                      <Form.Check
                        id='gender_optional'
                        type='checkbox'
                        label='Optional?'
                        name='gender_optional'
                        defaultChecked={!!values.gender_optional}
                        onChange={toggleBoolean('gender_optional')}
                      />
                    )}
                    <Form.Check
                      id='gender_hidden'
                      type='checkbox'
                      label='Hidden?'
                      name='gender_hidden'
                      defaultChecked={!!values.gender_hidden}
                      onChange={toggleBoolean('gender_hidden')}
                    />
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='ethnicity_options'>
                    Ethnicity Options&nbsp;
                    <OverlayTrigger overlay={<Tooltip>Enter options separated by commas</Tooltip>}>
                      <FontAwesome icon={faInfoCircle} preNbsp />
                    </OverlayTrigger>
                  </Form.Label>

                  <div className='controls'>
                    {!values.ethnicity_hidden && (
                      <Form.Control
                        name='ethnicity_options'
                        as='textarea'
                        rows={5}
                        onChange={handleChange}
                        value={values.ethnicity_options}
                        className='manage-shared-portal-group-modal-schools-ethnicity-options'
                      />
                    )}
                    {!values.ethnicity_hidden && (
                      <Form.Check
                        id='ethnicity_optional'
                        type='checkbox'
                        label='Optional?'
                        name='ethnicity_optional'
                        defaultChecked={!!values.ethnicity_optional}
                        onChange={toggleBoolean('ethnicity_optional')}
                      />
                    )}
                    <Form.Check
                      id='ethnicity_hidden'
                      type='checkbox'
                      label='Hidden?'
                      name='ethnicity_hidden'
                      defaultChecked={!!values.ethnicity_hidden}
                      onChange={toggleBoolean('ethnicity_hidden')}
                    />
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='household_optional'>
                    Household Options
                  </Form.Label>

                  <div className='controls'>
                    {!values.household_hidden && (
                      <Form.Check
                        id='household_optional'
                        type='checkbox'
                        label='Optional?'
                        name='household_optional'
                        defaultChecked={!!values.household_optional}
                        onChange={toggleBoolean('household_optional')}
                      />
                    )}
                    <Form.Check
                      id='household_hidden'
                      type='checkbox'
                      label='Hidden?'
                      name='household_hidden'
                      defaultChecked={!!values.household_hidden}
                      onChange={toggleBoolean('household_hidden')}
                    />
                  </div>
                </div>

                <div className='mb-3 row'>
                  <Form.Label htmlFor='demo'>
                    Demo&nbsp;
                  </Form.Label>

                  <div className='controls'>
                    <Form.Check
                      id='demo'
                      type='checkbox'
                      name='demo'
                      defaultChecked={!!values.demo}
                      onChange={toggleBoolean('demo')}
                    />
                  </div>
                </div>

              </div>
            </form>
          </ModalComponent>
        );
      }}
    </Formik>
  );
};

ManageSharedPortalGroupModal.propTypes = {
  ...SharedPortalGroupProps,
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    subdomain: PropTypes.string
  })),
  customerIds: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func
};

ManageSharedPortalGroupModal.defaultProps = {
  id: null,
  subdomain: '',
  location: '',
  customers: [],
  customerIds: [],
  onSubmit: () => false
};

export default ManageSharedPortalGroupModal;
